import type { ToastPluginApi } 	from 'vue-toast-notification';
import type RpcMeta             from "@/core/services/Rpc/RpcMeta";
import type RpcMetaTime         from "@/core/services/Rpc/RpcMetaTime";
import Config                   from "@/core/misc/Config";
import RpcRequest               from "@/core/services/Rpc/RpcRequest";
import RpcResponse              from "@/core/services/Rpc/RpcResponse";
import SentryBreadcrumbList     from "@/core/services/Sentry/SentryBreadcrumbList";
import TimeStamp                from "@/core/types/TimeStamp";
import humps                    from 'humps';
import LegacyUtils              from "@/core/utils/LegacyUtils";

/** Опции запроса */
export interface RequestOptions {
    signal?: AbortSignal;
    onProgress?: (progress: ProgressInfo) => void;
    timeout?: number;

    /**
     * Отменить предыдущий запрос с таким же методом.
     * Используется для списочных методов (getList) где при быстром
     * переключении фильтров предыдущий запрос уже не актуален.
     */
    abortPrevious?: boolean;
}

/** Информация о прогрессе */
export interface ProgressInfo {
    percentage: number;
    loaded: number;
    total: number;
}


/**
 * Минимальный RPC сервис для выполнения JSON-RPC запросов
 * Только HTTP транспорт + базовая обработка JSON-RPC протокола
 */
class RpcService {

    /**
     * HTTP клиент для выполнения запросов
     */
    static #httpClient: any = null;

    /**
     * Счетчик запросов для генерации уникальных ID
     */
    static #requestCounter: number = 0;

    /**
     * Префикс для ID запросов
     */
    static #requestPrefix: string = 'xhr-';

    /**
     * Текущий статус связи.
     * Реактивный — чтобы компоненты-канарейки перерисовывались автоматически.
     */
    static connectionStatus: Ref<'ok' | 'warning' | 'critical'> = ref('ok');

    /**
     * Контроллеры отмены запросов по ключу метода.
     * Используется при abortPrevious: true для отмены предыдущего запроса.
     */
    static #abortControllers: Map<string, AbortController> = new Map();

    /**
     * Серия подряд идущих сбоев связи (таймаут, отмена, транспортная ошибка).
     * Сбрасывается при любом успешном ответе.
     */
    static #failureStreak: number = 0;

    /**
     * Расхождение часов пользователя с серверным временем (в миллисекундах).
     * Положительное — пользователь отстаёт от сервера, отрицательное — убежал вперёд.
     */
    static clockSkewMs: Ref<number> = ref(0);

    /**
     * Коллбэки для взаимодействия с Core (инверсия зависимостей)
     */
    static #showLoader: () => void;
    static #hideLoader: () => void;
    static #handleError: (error: any, request: any, silent?: boolean) => void;
    static #showNotification: () => ToastPluginApi;
    static #getTimeZone: () => any;
    static #getSessionToken: () => string | null;
    static #addBreadcrumb: (message: string, category: string, level: string, data?: Record<string, any>) => void;

    /**
     * Инициализация RPC сервиса
     */
    static async init(
        showLoader: () => void,
        hideLoader: () => void,
        handleError: (error: any, request: any, silent?: boolean) => void,
        showNotification: () => ToastPluginApi,
        getTimeZone: () => any,
        getSessionToken: () => string | null,
        addBreadcrumb: (message: string, category: string, level: string, data?: Record<string, any>) => void
    ): Promise<void> {
        this.#showLoader = showLoader;
        this.#hideLoader = hideLoader;
        this.#handleError = handleError;
        this.#showNotification = showNotification;
        this.#getTimeZone = getTimeZone;
        this.#getSessionToken = getSessionToken;
        this.#addBreadcrumb = addBreadcrumb;

        this.#initJsonRpcClient();
    }

    /**
     * Legacy RPC запрос с обратной совместимостью
     * Для старого кода который ожидает camelizeKeys и prepareResponseData
     */
    static async makeLegacyRequest(
        method: string,
        params: any = {},
        options: RequestOptions = {}
    ): Promise<{ data: any; meta: any }> {
        /** Объекта pagination больше не должно быть в params, нужно передать аргументом в новом методе rpc **/
        const pagination = params?.pagination || null;
        const sort = params?.sort || null;
        delete params.pagination;
        delete params.sort;

        const response = await this.makeRequest(method, params, pagination, sort, true, null, null, true, options);

        // Для legacy возвращаем старый формат
        return { data: response.data, meta: response.meta };
    }

    /**
     * RPC запрос с подавлением стандартного уведомления об ошибке
     * Sentry-репорт отправляется, но тост не показывается — для случаев с кастомными сообщениями
     */
    static async makeRequestSilent(
        method: string,
        params: Record<string, unknown> = {},
        pagination: any = null,
        sort: any = null,
        showLoader: boolean = true,
        options: RequestOptions = {}
    ): Promise<RpcResponse> {
        return this.makeRequest(method, params, pagination, sort, showLoader, null, null, false, options, true);
    }

    /**
     * Основной метод выполнения RPC запроса
     *
     * @param method - Имя RPC метода
     * @param params - Параметры запроса
     * @param pagination - Параметры пагинации
     * @param sort - Параметры сортировки
     * @param showLoader - Показывать ли лоадер
     * @param onSuccess - Колбэк при успехе
     * @param onError - Колбэк при ошибке (в дополнение к стандартной обработке)
     * @param compatibilityMode - Режим совместимости с legacy API
     * @param options - Дополнительные опции (signal, onProgress, abortPrevious)
     * @param silent - Подавить тост об ошибке (Sentry-репорт всё равно отправляется)
     */
    static async makeRequest(
        method: string,
        params: Record<string, unknown> = {},
        pagination: any = null,
        sort: any = null,
        showLoader: boolean = true,
        onSuccess: ((response: RpcResponse) => void) | null = null,
        onError: ((error: Error) => void) | null = null,
        compatibilityMode: boolean = false,
        options: RequestOptions = {},
        silent: boolean = false
    ): Promise<RpcResponse> {

        // Отмена предыдущего запроса с таким же методом
        if (options.abortPrevious) {
            this.abortByMethod(method);

            const controller = new AbortController();
            this.#abortControllers.set(method, controller);

            // Подставляем signal в options, не затирая остальное
            options = { ...options, signal: controller.signal };
        }

        if (showLoader) {
            this.#showLoader();
        }

        const request = RpcRequest.fromRpcService(
            this.#getRequestId(),
            method,
            params,
            this.#getSessionToken(),
            pagination,
            sort,
            this.#getTimeZone()
        );

        let response: RpcResponse;

        try {
            const rawResponse = await this.#httpClient!.post(request, compatibilityMode, options);
            if (rawResponse.cancelled) {
                response = RpcResponse.fromCancellation(request.id);
                // Отмена запросы должна считаться как невыполненный запрос
                this.#registerFailure();
            } else if (rawResponse.timeout) {
                response = RpcResponse.fromTimeout(request.id);
                this.#registerFailure();
            } else {
                response = RpcResponse.fromServer(rawResponse.data);
                this.#registerSuccess(response.meta);
            }

        } catch (error) {
            response = RpcResponse.fromTransportError(error as Error, request.id);
            /**
             * Транспортная ошибка — это тоже пропуск, канал не работает.
             * DNS, CORS, network error — всё сюда.
             */
            this.#registerFailure();
        } finally {
            if (showLoader) {
                this.#hideLoader();
            }

            // Чистим контроллер после завершения запроса
            if (options.abortPrevious && this.#abortControllers.get(method)?.signal === options.signal) {
                this.#abortControllers.delete(method);
            }
        }

        // Добавляем breadcrumb для отслеживания последовательности событий.
        // id — из параметров запроса; если его нет, undefined выпадает
        // при JSON.stringify конверта и в Sentry не попадает
        this.#addBreadcrumb(
            `RPC: ${method}`,
            'rpc',
            'info',
            { requestId: request.id, id: params['id'] }
        );

        // Прокидываем серверные breadcrumbs в Sentry
        SentryBreadcrumbList.addServerBreadcrumbs(response.debug?.breadcrumbs);

        // Обработка уведомлений
        if (Array.isArray(response.notifications) && response.notifications.length > 0) {
            response.notifications.forEach((notification: string) => {

                /** при старте RpcService #showNotification может не дойти, поэтому optional-chaining */

                this.#showNotification()?.info(notification);
            });
        }

        // Обработка ошибок/успеха
        if (response.error) {
            // Sentry-репорт всегда, тост — если не подавлен
            this.#handleError(response.error, request, silent);

            // Дополнительный колбэк, если передан
            if (onError) {
                onError(response.error);
            }
            /**
             *  Особый случай: транспорт успешен, но бэкенд вернул false
             *  Это означает что операция не выполнена по бизнес-логике
             *  (например: обновлять было нечего)
             *  Показываем стандартное сообщение, если не подавлено флагом silent
             *
             *  Почему? Чтобы в сервисах не нужно было писать проверку на транспортный уровень, и на сами данные
             * */
        } else if (response.data === false) {
            // Операция не выполнена — показываем стандартное сообщение
            if (!silent) {
                this.#showNotification()?.error('Операция не выполнена')
            }
        } else if (onSuccess) {
            onSuccess(response);
        }

        return response;
    }

    /**
     * Отменяет активный запрос по имени метода
     */
    static abortByMethod(method: string): void {
        this.#abortControllers.get(method)?.abort();
        this.#abortControllers.delete(method);
    }

    /**
     * Инициализация HTTP клиента для JSON-RPC запросов
     */
    static #initJsonRpcClient(): void {
        this.#httpClient = {
            post: async (
                request: RpcRequest,
                compatibilityMode: boolean = false,
                options: RequestOptions = {}
            ) => {
                const timeoutController = new AbortController();
                const timeoutId = setTimeout(() => timeoutController.abort(), options?.timeout ?? Config.api.timeout ?? 300000);

                const combinedController = new AbortController();

                // Если таймаут сработал — абортим combined
                timeoutController.signal.addEventListener('abort', () => combinedController.abort());

                // Если внешний signal сработал — абортим combined
                if (options.signal) {
                    options.signal.addEventListener('abort', () => combinedController.abort());
                }

                const signal = combinedController.signal;

                try {
                    // === СЕРИАЛИЗАЦИЯ ДАННЫХ ===
                    let jsonData: string;
                    try {
                        jsonData = JSON.stringify(request);
                    } catch (stringifyError) {
                        throw new Error(`JSON serialization error: ${(stringifyError as Error).message}`);
                    }

                    // === HTTP ЗАПРОС ===
                    const response = await fetch(Config.api.baseURL, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: jsonData,
                        signal: signal
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }

                    // === ПАРСИНГ ОТВЕТА ===
                    const responseText = await response.text();

                    // Симулируем прогресс для совместимости (если передан onProgress)
                    if (options.onProgress) {
                        options.onProgress({
                            percentage: 100,
                            loaded: responseText.length,
                            total: responseText.length
                        });
                    }

                    let responseData: unknown;
                    try {
                        if (compatibilityMode) {
                            // Применяем legacy обработку
                            responseData = this.#applyLegacyProcessing(responseText);
                        } else {
                            // Обычный парсинг
                            responseData = JSON.parse(responseText);
                        }
                    } catch (parseError) {
                        throw new Error(`JSON parse error: ${(parseError as Error).message}. Response: ${responseText.substring(0, 200)}`);
                    }

                    return {
                        data: responseData,
                        status: response.status,
                        statusText: response.statusText
                    };

                } catch (error) {
                    if ((error as Error).name === 'AbortError') {
                        // Различаем таймаут и отмену пользователем
                        if (timeoutController.signal.aborted) {
                            // Таймаут — возвращаем как ошибку, не как отмену
                            return {
                                data: null,
                                status: 0,
                                statusText: 'Request timeout',
                                timeout: true
                            };
                        }
                        // Отмена пользователем — не ошибка
                        return {
                            data: null,
                            status: 0,
                            statusText: 'Request cancelled',
                            cancelled: true
                        };
                    }
                    // остальные ошибки прокидываем выше
                    throw error;
                } finally {
                    // Очищаем таймаут в любом случае
                    clearTimeout(timeoutId);
                }
            }
        };
    }

    /**
     * Применение обработки совместимости со старым API
     */
    static #applyLegacyProcessing(responseText: string): unknown {
        // Очистка PHP warnings
        let cleanedText = this.#cleanPhpWarnings(responseText);

        // Парсинг JSON
        let data = JSON.parse(cleanedText);

        if (data !== null && typeof data === 'object') {
            // camelizeKeys обработка
            data = humps.camelizeKeys(data, (key: string, convert: (key: string) => string) =>
                /^[A-Z]+$/.test(key) ? key : convert(key)
            );

            // prepareResponseData обработка дат (только для legacy)
            data = LegacyUtils.prepareResponseData(data);
        }

        return data;
    }

    /**
     * Очистка JSON ответа от PHP warnings/notices
     */
    static #cleanPhpWarnings(responseText: string): string {
        // Удаляем PHP warnings/notices перед JSON — ищем первый { с которого начинается валидный JSON
        const firstBrace = responseText.indexOf('{');
        if (firstBrace > 0) {
            return responseText.substring(firstBrace);
        }
        return responseText;
    }

    static #getRequestsCounter(): number {
        this.#requestCounter++;
        return this.#requestCounter;
    }

    static #getRequestId(): string {
        return this.#requestPrefix + this.#getRequestsCounter();
    }

    /**
     * Vue плагин для интеграции RPC сервиса
     */
    static getVuePlugin() {
        return {
            install(app: App) {
                app.config.globalProperties.$http = {
                    async rpc(method: string, payload: Record<string, unknown> = {}) {
                        return await RpcService.makeLegacyRequest(method, payload);
                    }
                };
            }
        };
    }

    static get isInitialized(): boolean {
        return this.#httpClient !== null;
    }

    /**
     * Обработать успешный ответ — сбросить серию сбоев
     * и пересчитать расхождение часов с сервером.
     */
    static #registerSuccess(meta?: RpcMeta): void {
        this.#failureStreak = 0;
        if (meta?.time) { this.#updateClockSkew(meta.time) }

        this.#updateConnectionStatus();
    }

    /**
     * Зарегистрировать сбой связи: таймаут, отмена или транспортная ошибка.
     * Все три случая означают что канал не сработал — наращиваем серию.
     */
    static #registerFailure(): void {
        this.#failureStreak += 1;
        this.#updateConnectionStatus();
    }

    /**
     * Пересчитать расхождение часов пользователя с сервером.
     * serverTime — серверный момент времени в формате бэкенда.
     */
    static #updateClockSkew(time: RpcMetaTime): void {
        const serverTimeEnd = TimeStamp.fromServer(time.serverTime!.end!)!;

        if (!serverTimeEnd) return;

        this.clockSkewMs.value = serverTimeEnd.getTime() - TimeStamp.now();
    }

    /**
     * Пересчитать статус связи
     */
    static #updateConnectionStatus(): void {
        const config = Config.connectionMonitor;

        if (this.#failureStreak >= config.criticalThreshold) {
            this.connectionStatus.value = 'critical';
        }
        else if (this.#failureStreak >= config.warningThreshold) {
            this.connectionStatus.value = 'warning';
        }
        else {
            this.connectionStatus.value = 'ok';
        }
    }
}

export default RpcService;